// Runtime branding: name, logos and colours come from the server and can change without a rebuild.
import { store } from "./ui";

export interface BrandColors {
  primary: string; ink: string; accent: string; accentText: string; soft: string; background: string;
  muted: string; line: string; good: string; attention: string; critical: string;
}

export interface PublicBrand {
  appName: string;
  shortName: string;
  tagline: { en: string; fr: string };
  colors: BrandColors;
  supportEmail: string;
  supportPhone: string;
  version: number;
  markUrl: string;
  wordmarkUrl: string | null;
  css: string;
  locales: { default: "en" | "fr"; enabled: ("en" | "fr")[] };
  /** Whether the server can send email (invitations, reset links). */
  emailEnabled: boolean;
}

const boot = JSON.parse(document.getElementById("boot")?.textContent ?? "{}");

export const brand = store<{ current: PublicBrand }>({ current: boot.brand });

function luminance(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

export function contrast(a: string, b: string) {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

export function onColor(bg: string, ink: string) {
  return contrast(bg, "#FFFFFF") >= contrast(bg, ink) ? "#FFFFFF" : ink;
}

/** CSS custom properties for a set of colours — same rules as the server, used for live previews. */
export function brandVars(c: BrandColors): Record<string, string> {
  return {
    "--c-primary": c.primary, "--c-ink": c.ink, "--c-accent": c.accent, "--c-accent-text": c.accentText,
    "--c-soft": c.soft, "--c-bg": c.background, "--c-muted": c.muted, "--c-line": c.line,
    "--c-good": c.good, "--c-attention": c.attention, "--c-critical": c.critical,
    "--c-on-primary": onColor(c.primary, c.ink), "--c-on-ink": onColor(c.ink, c.ink),
    "--c-on-accent": onColor(c.accent, c.ink), "--c-on-good": onColor(c.good, c.ink),
    "--c-on-attention": onColor(c.attention, c.ink), "--c-on-critical": onColor(c.critical, c.ink),
  };
}

/** Applies a new brand to the running page (after the admin saves). */
export function applyBrand(b: PublicBrand) {
  brand.set({ current: b });
  const style = document.getElementById("brand-css");
  if (style) style.textContent = b.css;
  document.title = b.appName;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", b.colors.primary);
  for (const rel of ["icon", "apple-touch-icon"]) document.querySelector(`link[rel="${rel}"]`)?.setAttribute("href", b.markUrl);
}
